import React, { useEffect, useRef, useState } from "react";
import { api, fmtINR, fmtDate, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Upload, CheckCircle, LinkSimple, Prohibit } from "@phosphor-icons/react";

export default function BankRecon() {
  const [txns, setTxns] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState("unmatched");
  const [suggest, setSuggest] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    const [t, s] = await Promise.all([
      api.get(`/bank/transactions${filter ? `?status=${filter}` : ""}`),
      api.get("/bank/summary"),
    ]);
    setTxns(t.data);
    setSummary(s.data);
  };
  useEffect(() => { load(); }, [filter]);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/bank/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.imported} transactions`);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const openSuggest = async (txnId) => {
    const { data } = await api.get(`/bank/suggestions/${txnId}`);
    setSuggest(data);
  };
  const matchIt = async (txnId, voucherId) => {
    await api.post("/bank/match", { txn_id: txnId, voucher_id: voucherId, action: "match" });
    toast.success("Matched");
    setSuggest(null);
    load();
  };
  const createVoucher = async (txnId) => {
    await api.post("/bank/match", { txn_id: txnId, action: "create_voucher" });
    toast.success("Voucher created & matched");
    setSuggest(null);
    load();
  };
  const ignore = async (txnId) => {
    await api.post("/bank/match", { txn_id: txnId, action: "ignore" });
    toast.success("Ignored");
    setSuggest(null);
    load();
  };

  return (
    <div className="space-y-5 lb-fade" data-testid="bank-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">Reconciliation</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Bank Reconciliation</h1>
        </div>
        <div>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden"
                 onChange={(e) => upload(e.target.files[0])} data-testid="bank-upload-input" />
          <button className="lb-btn" onClick={() => fileRef.current?.click()} data-testid="bank-upload-btn">
            <Upload size={14} /> Upload Statement (CSV/XLSX)
          </button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-4 gap-4">
          <div className="lb-kpi"><div className="lb-kpi-label">Statement Balance</div><div className="lb-kpi-value mono">{fmtINR(summary.statement_balance)}</div></div>
          <div className="lb-kpi"><div className="lb-kpi-label">Book Balance (Bank Ledger)</div><div className="lb-kpi-value mono">{fmtINR(summary.book_balance)}</div></div>
          <div className="lb-kpi">
            <div className="lb-kpi-label">Difference</div>
            <div className={"lb-kpi-value mono " + (Math.abs(summary.difference) < 0.01 ? "text-emerald-600" : "text-rose-600")}>
              {fmtINR(summary.difference)}
            </div>
          </div>
          <div className="lb-kpi">
            <div className="lb-kpi-label">Matched / Unmatched</div>
            <div className="lb-kpi-value mono">{summary.matched_count} / {summary.unmatched_count}</div>
          </div>
        </div>
      )}

      <div className="lb-card">
        <div className="p-3 flex gap-2 border-b border-neutral-200">
          {["unmatched", "matched", "ignored", ""].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
                    className={"px-3 py-1 text-xs font-semibold uppercase tracking-wider transition-colors " +
                      (filter === f ? "bg-[#0A66C2] text-white" : "border border-neutral-300 hover:border-[#0A66C2]")}
                    data-testid={`bank-filter-${f || "all"}`}>
              {f || "All"}
            </button>
          ))}
        </div>
        <table className="lb-table">
          <thead>
            <tr><th>Date</th><th>Description</th><th>Reference</th><th className="text-right">Amount</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {txns.map((t) => (
              <tr key={t.id} data-testid={`bank-row-${t.id}`}>
                <td className="text-sm font-mono">{t.date}</td>
                <td className="text-sm">{t.description}</td>
                <td className="font-mono text-xs">{t.reference}</td>
                <td className={"amt " + (t.amount >= 0 ? "text-emerald-600" : "text-rose-600")}>
                  {fmtINR(t.amount)}
                </td>
                <td>
                  <span className={"lb-badge " + (
                    t.status === "matched" ? "lb-badge-success" :
                    t.status === "ignored" ? "" : "lb-badge-warning"
                  )}>{t.status}</span>
                </td>
                <td>
                  {t.status === "unmatched" && (
                    <div className="flex gap-1">
                      <button className="lb-btn lb-btn-sm" onClick={() => openSuggest(t.id)}
                              data-testid={`bank-match-${t.id}`}>
                        <LinkSimple size={12} /> Match
                      </button>
                      <button className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => createVoucher(t.id)}>
                        <CheckCircle size={12} /> Voucher
                      </button>
                      <button className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => ignore(t.id)}>
                        <Prohibit size={12} />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {!txns.length && <tr><td colSpan={6} className="text-center py-10 text-neutral-500">
              No transactions. Upload a bank statement.
            </td></tr>}
          </tbody>
        </table>
      </div>

      {suggest && (
        <div className="fixed inset-0 z-40 bg-black/50 grid place-items-center p-4" onClick={() => setSuggest(null)}>
          <div className="lb-card p-5 w-full max-w-2xl bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="font-display font-bold text-lg mb-3">Match transaction</div>
            <div className="border border-neutral-200 p-3 mb-3 text-sm font-mono">
              {suggest.txn.date} · {suggest.txn.description} · {fmtINR(suggest.txn.amount)}
            </div>
            <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold mb-2">Suggested vouchers</div>
            <div className="max-h-72 overflow-auto border border-neutral-200">
              {suggest.suggestions.length === 0 && <div className="p-4 text-sm text-neutral-500">No matches. Create a voucher instead.</div>}
              {suggest.suggestions.map((v) => (
                <div key={v.id} className="flex items-center justify-between px-3 py-2 border-b border-neutral-100">
                  <div>
                    <div className="text-sm font-semibold">{v.ref_type}</div>
                    <div className="text-xs text-neutral-500 font-mono">{fmtDate(v.date)} · {v.narration}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="font-mono font-semibold">{fmtINR(v.total)}</div>
                    <button className="lb-btn lb-btn-sm" onClick={() => matchIt(suggest.txn.id, v.id)}>Match</button>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button className="lb-btn lb-btn-ghost" onClick={() => setSuggest(null)}>Close</button>
              <button className="lb-btn" onClick={() => createVoucher(suggest.txn.id)}>Create new voucher</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
